/**
 * Utility functions for generating and working with URL slugs
 */

/**
 * Generate a URL-friendly slug from a university name or abbreviation
 * Examples:
 * - "UCLA" -> "ucla"
 * - "New York University" -> "nyu"
 * - "University of California, Los Angeles" -> "ucla"
 * - "Penn State" -> "penn-state"
 * - "Texas A&M" -> "texas-am"
 */
export function generateUniversitySlug(name: string, abbreviation?: string): string {
  // If abbreviation exists, prefer it (handles all cases including "Penn State", "Texas A&M", etc.)
  if (abbreviation) {
    return abbreviation
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')  // Replace any non-alphanumeric with hyphen
      .replace(/^-+|-+$/g, '');      // Remove leading/trailing hyphens
  }
  
  // Otherwise, generate from name
  return name
    .toLowerCase()
    .trim()
    .replace(/^university of /i, '')
    .replace(/\buniversity\b/gi, '')
    .replace(/\bcollege\b/gi, '')
    .replace(/^the /i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50); // Limit length
}

/**
 * Get a slug from a university object
 */
export function getUniversitySlug(university: { name: string; abbreviation?: string }): string {
  return generateUniversitySlug(university.name, university.abbreviation);
}

/**
 * Find a university by slug from a list of universities
 */
export function findUniversityBySlug(
  universities: Array<{ name: string; abbreviation?: string }>,
  slug: string
): { name: string; abbreviation?: string } | null {
  const normalizedSlug = slug.toLowerCase().trim().replace(/^-+|-+$/g, '');
  
  for (const uni of universities) {
    const uniSlug = getUniversitySlug(uni);
    
    // Check if slug matches generated slug
    if (uniSlug === normalizedSlug) {
      return uni;
    }
    
    // Also check if slug matches abbreviation directly (normalized)
    if (uni.abbreviation) {
      const abbrevSlug = uni.abbreviation
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      if (abbrevSlug === normalizedSlug) {
        return uni;
      }
    }
  }
  
  return null;
}
